import { FieldProps } from '@rjsf/utils';
import { DataGrid, GridColDef, GridColType, GridRowsProp, GridRowModel, GridRowId, Toolbar } from '@mui/x-data-grid';
import { useState, useEffect, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { PillIconButton } from 'components/button/PillIconButton';
import { TestDataFrame } from 'api/model';
import useDialog from 'hooks/useDialog';
import { toast } from 'react-toastify';
import { AddColumnDialog, NewColumn } from 'components/dialog/AddColumnDialog';
import { CustomFormat, getErrorMessage } from './CustomFormats';

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onAddRow: () => void;
    onAddColumn: () => void;
  }
}

const ACTIONS_FIELD = '__actions';
const MAX_ROWS = 15;
const MAX_COLUMNS = 10;

type DataframeToolbarProps = {
  onAddRow: () => void;
  onAddColumn: () => void;
};

const DataframeToolbar = ({ onAddRow, onAddColumn }: DataframeToolbarProps) => {
  return (
    <Toolbar>
      <Box>
        <Button startIcon={<AddIcon />} onClick={onAddRow} sx={{ color: 'accent.main' }}>
          Add Row
        </Button>
      </Box>
      <Box>
        <Button startIcon={<AddIcon />} onClick={onAddColumn} sx={{ color: 'accent.main' }}>
          Add Column
        </Button>
      </Box>
    </Toolbar>
  );
};

const determineColumnType = (values: any[]): GridColType => {
  const nonNullValue = values.find((v) => v !== null && v !== undefined);
  return nonNullValue === undefined ? 'string' : (typeof nonNullValue as GridColType);
};

const DataframeField = (props: FieldProps) => {
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [columnTypes, setColumnTypes] = useState<Record<string, GridColType>>({});
  const [showAddColumn, AddColumn] = useDialog(AddColumnDialog);

  useEffect(() => {
    const dataframe = (props.formData || {}) as TestDataFrame;

    const newColumnTypes = { ...columnTypes };

    const initialColumns: GridColDef[] = Object.entries(dataframe).map(([key, value]) => {
      if (!(key in newColumnTypes)) {
        newColumnTypes[key] = determineColumnType(value);
      }
      return createColumnDefinition(key, newColumnTypes[key]);
    });

    setColumnTypes(newColumnTypes);

    initialColumns.push({
      field: ACTIONS_FIELD,
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <PillIconButton
          size="small"
          aria-label="delete row"
          title="Delete row"
          onClick={() => handleDeleteRow(params.id)}
        >
          <DeleteIcon fontSize="small" />
        </PillIconButton>
      ),
    });

    setColumns(initialColumns);

    const nRows = Math.max(...Object.values(dataframe).map((v) => v.length), 0);
    const initialRows = [];
    for (let i = 0; i < nRows; i++) {
      const row: { [key: string]: any } = { id: i };
      Object.entries(dataframe).forEach(([key, value]) => {
        row[key] = value[i] !== undefined ? value[i] : null;
      });
      initialRows.push(row);
    }
    setRows(initialRows);
  }, [props.formData]);

  const createColumnDefinition = (name: string, type: GridColType) =>
    ({
      field: name,
      headerName: name,
      editable: true,
      flex: 1,
      headerAlign: 'left',
      type: type === 'boolean' ? 'singleSelect' : type,
      valueOptions: type === 'boolean' ? [true, false, null] : undefined,
      renderHeader: () => (
        <Box className="flex items-center justify-between w-full gap-2">
          {name}
          <PillIconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteColumn(name);
            }}
            title="Delete column"
          >
            <DeleteIcon fontSize="small" />
          </PillIconButton>
        </Box>
      ),
    }) as GridColDef;

  useEffect(() => {
    if (columns.length === 0 || rows.length === 0) return;

    const columnNames = columns.filter((col) => col.field !== ACTIONS_FIELD).map((col) => col.field);

    const dataframe: TestDataFrame = {};
    columnNames.forEach((colName) => {
      dataframe[colName] = rows.map((row) => (row[colName] !== undefined ? row[colName] : null));
    });

    props.onChange(dataframe);
  }, [rows, columns, props.onChange]);

  const handleDeleteRow = (id: GridRowId) => setRows((prev) => prev.filter((row) => row.id !== id));

  const handleRowUpdate = (newRow: GridRowModel) => {
    setRows((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
    return newRow;
  };

  const handleAddRow = useCallback(() => {
    if (rows.length >= MAX_ROWS) {
      toast.error(`Maximum number of rows (${MAX_ROWS}) reached.`);
      return;
    }

    const id = Math.max(...rows.map((row) => row.id)) + 1;
    const newRow: GridRowModel = { id };
    columns
      .filter((col) => col.field !== ACTIONS_FIELD)
      .forEach((col) => {
        newRow[col.field] = null;
      });

    setRows((prevRows) => [...prevRows, newRow]);
  }, [rows, columns]);

  const handleDeleteColumn = (columnField: string) => {
    setColumns((prev) => prev.filter((col) => col.field !== columnField));
    setColumnTypes((prev) => {
      const newColumnTypes = { ...prev };
      delete newColumnTypes[columnField];
      return newColumnTypes;
    });
    setRows((prev) =>
      prev.map((row) => {
        const newRow = { ...row };
        delete newRow[columnField];
        return newRow;
      }),
    );
  };

  const handleAddColumn = useCallback(
    (data: NewColumn) => {
      if (columns.some((col) => col.field === data.name)) {
        toast.error('Column already exists.');
        return;
      }

      const validationError = getErrorMessage(CustomFormat.ColumnName, data.name);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      setColumnTypes((prev) => ({ ...prev, [data.name]: data.type }));
      const column = createColumnDefinition(data.name, data.type);

      const actionsColumn = columns.find((col) => col.field === ACTIONS_FIELD);
      const newColumns = [...columns.filter((col) => col.field !== ACTIONS_FIELD), column];

      if (actionsColumn) {
        newColumns.push(actionsColumn);
      }

      setColumns(newColumns);

      const updatedRows = rows.map((row) => ({
        ...row,
        [data.name]: null,
      }));

      setRows(updatedRows);
      showAddColumn(false);
    },
    [rows, columns],
  );

  const handleShowAddColumn = useCallback(() => {
    if (columns.length >= MAX_COLUMNS) {
      toast.error(`Maximum number of columns (${MAX_COLUMNS}) reached.`);
      return;
    }

    showAddColumn(true);
  }, [columns]);

  return (
    <Box>
      <DataGrid
        rows={rows}
        columns={columns}
        processRowUpdate={handleRowUpdate}
        slots={{
          toolbar: DataframeToolbar,
        }}
        slotProps={{
          toolbar: { onAddRow: handleAddRow, onAddColumn: handleShowAddColumn },
        }}
        showToolbar
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        disableColumnSelector
        hideFooter
        showCellVerticalBorder
      />
      <AddColumn
        // @ts-expect-error: incompatible onSubmit args
        onSubmit={handleAddColumn}
        onClose={() => showAddColumn(false)}
      />
    </Box>
  );
};

export default DataframeField;
